
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator } from "lucide-react";
import { usePlanosAtivos } from "@/hooks/usePlanos";
import { useFormasPagamentoAtivas } from "@/hooks/useFormasPagamento";

interface OrcamentoCalculatorProps {
  qtdPaineis: number;
  setQtdPaineis: (value: number) => void;
  planoSelecionado: string;
  setPlanoSelecionado: (value: string) => void;
  valorPersonalizado: number;
  setValorPersonalizado: (value: number) => void;
  vigencia: string;
  setVigencia: (value: string) => void;
  formaPagamento: string;
  setFormaPagamento: (value: string) => void;
  parcelasCartao: string;
  setParcelasCartao: (value: string) => void;
  valorTabela: number;
  valorFinalProposta: number;
  setValorFinalProposta: (value: number) => void;
  calcularOrcamento: () => number;
  calcularValorBase: () => number;
  calcularJuros: (valor: number) => number;
}

export function OrcamentoCalculator({
  qtdPaineis,
  setQtdPaineis,
  planoSelecionado,
  setPlanoSelecionado,
  valorPersonalizado,
  setValorPersonalizado,
  vigencia,
  setVigencia,
  formaPagamento,
  setFormaPagamento,
  parcelasCartao,
  setParcelasCartao,
  valorTabela,
  valorFinalProposta,
  setValorFinalProposta,
  calcularOrcamento,
  calcularValorBase,
  calcularJuros
}: OrcamentoCalculatorProps) {
  const { data: planosAtivos } = usePlanosAtivos();
  const { data: formasPagamentoAtivas } = useFormasPagamentoAtivas();
  const formaAtual = formasPagamentoAtivas?.find(
    (f) => f.id.toString() === formaPagamento || f.tipo === formaPagamento
  );
  const formaPagamentoValue = formaAtual?.id.toString() ?? "";
  const descontoComercial = Math.max(0, Math.round((valorTabela - valorFinalProposta) * 100) / 100);
  const jurosSobreFinal = calcularJuros(valorFinalProposta);

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-gray-900">
          <Calculator className="h-5 w-5 text-green-600" />
          Calculadora de Orçamento
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="paineis">Quantidade de Painéis</Label>
          <Input
            id="paineis"
            type="number"
            value={qtdPaineis}
            onChange={(e) => setQtdPaineis(Number(e.target.value))}
            placeholder="Digite a quantidade de painéis"
            className="mt-1"
          />
        </div>

        <div>
          <Label>Vigência do Contrato</Label>
          <Select value={vigencia} onValueChange={setVigencia}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 Ano</SelectItem>
              <SelectItem value="2">2 Anos (Desconto 10%)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Plano de Proteção</Label>
          <RadioGroup value={planoSelecionado} onValueChange={setPlanoSelecionado} className="mt-2">
            {planosAtivos?.map((plano) => (
              <div key={plano.id} className="flex items-center space-x-2">
                <RadioGroupItem value={plano.id} id={plano.id} />
                <Label htmlFor={plano.id} className="flex-1 cursor-pointer">
                  <div className="p-3 border rounded-lg hover:bg-gray-50">
                    <h4 className="font-medium text-gray-900">{plano.nome}</h4>
                    <p className="text-sm text-gray-600">
                      {plano.descricao || "Cobertura contra vendaval, granizo, danos elétricos e explosão"}
                    </p>
                    <p className="text-sm font-medium text-green-600 mt-1">
                      R$ {plano.valor_por_painel?.toFixed(2) || "0,00"} por painel
                    </p>
                  </div>
                </Label>
              </div>
            ))}

            <div className="flex items-center space-x-2">
              <RadioGroupItem value="personalizado" id="personalizado" />
              <Label htmlFor="personalizado" className="flex-1 cursor-pointer">
                <div className="p-3 border rounded-lg hover:bg-gray-50">
                  <h4 className="font-medium text-gray-900">Plano Personalizado</h4>
                  <p className="text-sm text-gray-600">
                    Cobertura personalizada - Defina o valor por painel
                  </p>
                  {planoSelecionado === "personalizado" && (
                    <div className="mt-2">
                      <Label htmlFor="valor-personalizado" className="text-xs">Valor por painel (R$)</Label>
                      <Input
                        id="valor-personalizado"
                        type="number"
                        value={valorPersonalizado}
                        onChange={(e) => setValorPersonalizado(Number(e.target.value))}
                        className="mt-1 w-32"
                        min="1"
                        step="0.01"
                      />
                    </div>
                  )}
                  <p className="text-sm font-medium text-orange-600 mt-1">
                    R$ {planoSelecionado === "personalizado" ? valorPersonalizado.toFixed(2) : "20,00"} por painel
                  </p>
                </div>
              </Label>
            </div>
          </RadioGroup>
        </div>

        <div>
          <Label>Forma de Pagamento</Label>
          <Select value={formaPagamentoValue} onValueChange={setFormaPagamento}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {formasPagamentoAtivas?.map((forma) => (
                <SelectItem key={forma.id} value={forma.id.toString()}>
                  {forma.nome}
                  {forma.taxa_juros && forma.taxa_juros > 0 && ` (${forma.taxa_juros}% juros)`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {formaAtual?.tipo === "cartao" && (
          <div>
            <Label>Parcelas no Cartão</Label>
            <Select value={parcelasCartao} onValueChange={setParcelasCartao}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({length: 12}, (_, i) => i + 1).map(parcela => (
                  <SelectItem key={parcela} value={parcela.toString()}>
                    {parcela}x {parcela > 1 && `(+${((parcela - 1) * 1).toFixed(0)}% juros)`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Valor Base:</span>
              <span className="font-medium">R$ {calcularValorBase().toFixed(2)}</span>
            </div>
            
            {vigencia === "2" && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Desconto 2 anos:</span>
                <span className="font-medium text-green-600">-10%</span>
              </div>
            )}

            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Valor de tabela:</span>
              <span className="font-medium">R$ {valorTabela.toFixed(2)}</span>
            </div>

            <div className="pt-1">
              <Label htmlFor="valor-final-proposta" className="text-xs text-gray-700">
                Valor final da proposta (R$)
              </Label>
              <Input
                id="valor-final-proposta"
                type="number"
                min={0}
                step="0.01"
                value={Number.isFinite(valorFinalProposta) ? valorFinalProposta : ""}
                onChange={(e) => setValorFinalProposta(parseFloat(e.target.value) || 0)}
                className="mt-1 bg-white"
              />
            </div>

            {descontoComercial > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Desconto comercial:</span>
                <span className="font-medium text-green-700">-R$ {descontoComercial.toFixed(2)}</span>
              </div>
            )}
            
            {jurosSobreFinal > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Juros:</span>
                <span className="font-medium text-red-600">+R$ {jurosSobreFinal.toFixed(2)}</span>
              </div>
            )}
            
            <hr className="border-gray-300" />
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-900">Valor Total:</span>
              <span className="text-2xl font-bold text-green-600">
                R$ {calcularOrcamento().toFixed(2)}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Vigência: {vigencia === "2" ? "2 anos" : "1 ano"} |{" "}
              {(() => {
                const forma = formasPagamentoAtivas?.find(
                  (f) => f.id.toString() === formaPagamento || f.tipo === formaPagamento
                );
                if (forma?.tipo === "cartao") return `${parcelasCartao}x no cartão`;
                return forma?.nome || formaPagamento?.replace(/-/g, " ") || "—";
              })()}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
